import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { AppLayout } from "@/components/AppLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { 
  Users, 
  UserPlus, 
  Search,
  Mail,
  Shield,
  Loader2,
  Clock,
  Copy,
  Trash2,
  CheckCircle
} from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function UserManagement({ user, setUser }) {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [inviteDialog, setInviteDialog] = useState({ open: false });
  const [inviteData, setInviteData] = useState({ email: "", role: "user" });
  const [inviting, setInviting] = useState(false);
  const [lastInviteResult, setLastInviteResult] = useState(null);
  const [createDialog, setCreateDialog] = useState({ open: false });
  const [createData, setCreateData] = useState({ email: "", name: "", role: "user" });
  const [creating, setCreating] = useState(false);
  const [createdResult, setCreatedResult] = useState(null);

  useEffect(function() {
    if (user?.role !== 'admin') {
      navigate('/dashboard');
      return;
    }
    fetchUsers();
    fetchInvitations();
  }, [user, navigate]);

  const fetchUsers = async function() {
    try {
      const response = await fetch(API_URL + "/api/users", {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error("Erreur lors du chargement des utilisateurs");
    } finally {
      setLoading(false);
    }
  };

  const fetchInvitations = async function() {
    try {
      const response = await fetch(API_URL + "/api/invitations/pending", {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setInvitations(data);
      }
    } catch (error) {
      console.error('Error fetching invitations:', error);
    }
  };

  const handleRoleChange = async function(userId, newRole) {
    try {
      const response = await fetch(API_URL + "/api/users/" + userId + "/role?role=" + newRole, {
        method: 'PUT',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success("Rôle mis à jour");
        setUsers(users.map(function(u) {
          return u.user_id === userId ? Object.assign({}, u, { role: newRole }) : u;
        }));
      } else {
        toast.error("Erreur lors de la mise à jour du rôle");
      }
    } catch (error) {
      toast.error("Erreur lors de la mise à jour du rôle");
    }
  };

  const handleInvite = async function() {
    if (!inviteData.email.trim()) {
      toast.error("L'email est requis");
      return;
    }

    setInviting(true);

    try {
      const response = await fetch(API_URL + "/api/users/invite?email=" + encodeURIComponent(inviteData.email) + "&role=" + inviteData.role, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        const result = await response.json();
        setLastInviteResult(result);
        toast.success(result.message);
        fetchInvitations();
        fetchUsers();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Erreur lors de l'envoi de l'invitation");
        setInviteDialog({ open: false });
      }
    } catch (error) {
      toast.error("Erreur lors de l'envoi de l'invitation");
    } finally {
      setInviting(false);
    }
  };

  const handleCancelInvitation = async function(inviteId) {
    try {
      const response = await fetch(API_URL + "/api/invitations/" + inviteId, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success("Invitation annulée");
        setInvitations(invitations.filter(function(inv) {
          return inv.invite_id !== inviteId;
        }));
      } else {
        toast.error("Erreur lors de l'annulation");
      }
    } catch (error) {
      toast.error("Erreur lors de l'annulation");
    }
  };

  const copyInviteLink = function(link) {
    const fullLink = window.location.origin + link;
    navigator.clipboard.writeText(fullLink);
    toast.success("Lien copié dans le presse-papier");
  };

  const handleCreateUser = async function() {
    if (!createData.email.trim() || !createData.name.trim()) {
      toast.error("Le nom et l'email sont requis");
      return;
    }
    setCreating(true);
    try {
      const response = await fetch(API_URL + "/api/users", {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(createData)
      });
      if (response.ok) {
        const result = await response.json();
        setCreatedResult(result);
        toast.success("Utilisateur créé");
        fetchUsers();
      } else {
        const error = await response.json();
        toast.error(typeof error.detail === "string" ? error.detail : "Erreur lors de la création");
      }
    } catch (error) {
      toast.error("Erreur lors de la création");
    } finally {
      setCreating(false);
    }
  };

  const closeCreateDialog = function() {
    setCreateDialog({ open: false });
    setCreateData({ email: "", name: "", role: "user" });
    setCreatedResult(null);
  };

  const copyPassword = function(pwd) {
    navigator.clipboard.writeText(pwd);
    toast.success("Mot de passe copié");
  };

  const handleDeleteUser = async function(userId) {
    if (!window.confirm("Supprimer définitivement cet utilisateur ?")) return;
    try {
      const response = await fetch(API_URL + "/api/users/" + userId, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (response.ok) {
        toast.success("Utilisateur supprimé");
        setUsers(users.filter(function(u) { return u.user_id !== userId; }));
      } else {
        const error = await response.json();
        toast.error(typeof error.detail === "string" ? error.detail : "Erreur");
      }
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const closeInviteDialog = function() {
    setInviteDialog({ open: false });
    setInviteData({ email: "", role: "user" });
    setLastInviteResult(null);
  };

  const filteredUsers = users.filter(function(u) {
    const searchLower = searchQuery.toLowerCase();
    return (u.name && u.name.toLowerCase().includes(searchLower)) ||
           u.email.toLowerCase().includes(searchLower);
  });

  const getRoleBadge = function(role) {
    const variants = {
      admin: { label: "Administrateur", className: "bg-purple-100 text-purple-800" },
      lawyer: { label: "Juriste", className: "bg-blue-100 text-blue-800" },
      user: { label: "Utilisateur", className: "bg-gray-100 text-gray-800" }
    };
    const variant = variants[role] || variants.user;
    return <Badge className={variant.className}>{variant.label}</Badge>;
  };

  const formatDate = function(dateString) {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  if (user?.role !== 'admin') {
    return null;
  }

  return (
    <AppLayout user={user} setUser={setUser}>
      <div className="p-8" data-testid="user-management-page">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading text-3xl font-semibold tracking-tight mb-2">
              Gestion des utilisateurs
            </h1>
            <p className="text-muted-foreground">
              Gérez les membres de votre organisation
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button 
              variant="outline"
              onClick={function() { setCreateDialog({ open: true }); }}
              data-testid="create-user-btn"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Créer un utilisateur
            </Button>
            <Button 
              onClick={function() { setInviteDialog({ open: true }); }}
              data-testid="invite-user-btn"
            >
              <Mail className="w-4 h-4 mr-2" />
              Inviter par lien
            </Button>
          </div>
        </div>

        <Tabs defaultValue="members" className="space-y-6">
          <TabsList>
            <TabsTrigger value="members" data-testid="tab-members">
              Membres ({users.length})
            </TabsTrigger>
            <TabsTrigger value="invitations" data-testid="tab-invitations">
              Invitations ({invitations.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="members">
            <div className="relative mb-6">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Rechercher un utilisateur..."
                value={searchQuery}
                onChange={function(e) { setSearchQuery(e.target.value); }}
                className="pl-10 max-w-md"
                data-testid="search-users-input"
              />
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="spinner w-8 h-8"></div>
              </div>
            ) : filteredUsers.length > 0 ? (
              <Card>
                <Table data-testid="users-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Utilisateur</TableHead>
                      <TableHead>Rôle</TableHead>
                      <TableHead>Date d'inscription</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredUsers.map(function(member) {
                      const initial = member.name ? member.name.charAt(0) : member.email.charAt(0).toUpperCase();
                      return (
                        <TableRow key={member.user_id} data-testid={"user-row-" + member.user_id}>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                                <span className="text-sm font-medium text-primary">
                                  {initial}
                                </span>
                              </div>
                              <div>
                                <div className="font-medium">{member.name || "Sans nom"}</div>
                                <div className="text-sm text-muted-foreground">{member.email}</div>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            {getRoleBadge(member.role)}
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {formatDate(member.created_at)}
                          </TableCell>
                          <TableCell className="text-right">
                            {member.user_id !== user.user_id ? (
                              <div className="flex items-center justify-end gap-2">
                                <Select
                                  value={member.role}
                                  onValueChange={function(value) { handleRoleChange(member.user_id, value); }}
                                >
                                  <SelectTrigger className="w-40" data-testid={"role-select-" + member.user_id}>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="user">Utilisateur</SelectItem>
                                    <SelectItem value="lawyer">Juriste</SelectItem>
                                    <SelectItem value="admin">Administrateur</SelectItem>
                                  </SelectContent>
                                </Select>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="text-destructive"
                                  onClick={function() { handleDeleteUser(member.user_id); }}
                                  title="Supprimer"
                                  data-testid={"delete-user-" + member.user_id}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            ) : (
                              <Badge variant="outline">Vous</Badge>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Card>
            ) : (
              <div className="empty-state" data-testid="empty-users">
                <div className="empty-state-icon">
                  <Users className="w-full h-full" />
                </div>
                <h3 className="font-semibold mb-2">Aucun utilisateur</h3>
                <p className="text-muted-foreground mb-4">
                  {searchQuery 
                    ? "Aucun utilisateur ne correspond à votre recherche" 
                    : "Invitez des membres à rejoindre votre organisation"}
                </p>
              </div>
            )}
          </TabsContent>

          <TabsContent value="invitations">
            {invitations.length > 0 ? (
              <Card>
                <Table data-testid="invitations-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Email</TableHead>
                      <TableHead>Rôle</TableHead>
                      <TableHead>Date d'envoi</TableHead>
                      <TableHead>Expire le</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {invitations.map(function(invitation) {
                      return (
                        <TableRow key={invitation.invite_id} data-testid={"invitation-row-" + invitation.invite_id}>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
                                <Mail className="w-5 h-5 text-yellow-600" />
                              </div>
                              <div>
                                <div className="font-medium">{invitation.email}</div>
                                <div className="text-xs text-muted-foreground">En attente</div>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            {getRoleBadge(invitation.role)}
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {formatDate(invitation.created_at)}
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            <div className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {formatDate(invitation.expires_at)}
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              <Button 
                                variant="ghost" 
                                size="icon"
                                onClick={function() { copyInviteLink("/register?invite=" + invitation.invite_token); }}
                                title="Copier le lien"
                              >
                                <Copy className="w-4 h-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="icon"
                                onClick={function() { handleCancelInvitation(invitation.invite_id); }}
                                className="text-destructive"
                                title="Annuler"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Card>
            ) : (
              <div className="empty-state" data-testid="empty-invitations">
                <div className="empty-state-icon">
                  <Mail className="w-full h-full" />
                </div>
                <h3 className="font-semibold mb-2">Aucune invitation en attente</h3>
                <p className="text-muted-foreground mb-4">
                  Invitez de nouveaux membres à rejoindre votre organisation
                </p>
                <Button onClick={function() { setInviteDialog({ open: true }); }}>
                  <UserPlus className="w-4 h-4 mr-2" />
                  Inviter un utilisateur
                </Button>
              </div>
            )}
          </TabsContent>
        </Tabs>

        <Dialog open={inviteDialog.open} onOpenChange={function(open) { if (!open) closeInviteDialog(); else setInviteDialog({ open }); }}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Inviter un utilisateur</DialogTitle>
              <DialogDescription>
                Envoyez une invitation à rejoindre votre organisation
              </DialogDescription>
            </DialogHeader>

            {lastInviteResult ? (
              <div className="space-y-6 py-4">
                <div className="flex items-center justify-center">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-8 h-8 text-green-600" />
                  </div>
                </div>
                <div className="text-center">
                  <h3 className="font-semibold text-lg mb-2">Invitation envoyée !</h3>
                  <p className="text-muted-foreground text-sm">{lastInviteResult.message}</p>
                </div>
                
                {lastInviteResult.invite_link && (
                  <div className="space-y-2">
                    <Label>Lien d'invitation</Label>
                    <div className="flex gap-2">
                      <Input 
                        readOnly 
                        value={window.location.origin + lastInviteResult.invite_link}
                        className="text-sm"
                      />
                      <Button 
                        variant="outline"
                        onClick={function() { copyInviteLink(lastInviteResult.invite_link); }}
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Partagez ce lien avec l'utilisateur pour qu'il puisse créer son compte
                    </p>
                  </div>
                )}

                {lastInviteResult.email_preview && (
                  <div className="space-y-2">
                    <Label>Aperçu de l'email</Label>
                    <div className="p-4 bg-muted rounded-lg text-sm space-y-2">
                      <p><strong>À:</strong> {lastInviteResult.email_preview.to}</p>
                      <p><strong>Objet:</strong> {lastInviteResult.email_preview.subject}</p>
                      <Separator className="my-2" />
                      <pre className="whitespace-pre-wrap text-xs text-muted-foreground">
                        {lastInviteResult.email_preview.body}
                      </pre>
                    </div>
                  </div>
                )}

                <DialogFooter>
                  <Button onClick={closeInviteDialog}>
                    Fermer
                  </Button>
                </DialogFooter>
              </div>
            ) : (
              <>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="invite-email">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="invite-email"
                        type="email"
                        placeholder="utilisateur@email.com"
                        value={inviteData.email}
                        onChange={function(e) { setInviteData(Object.assign({}, inviteData, { email: e.target.value })); }}
                        className="pl-10"
                        data-testid="invite-email-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="invite-role">Rôle</Label>
                    <div className="relative">
                      <Shield className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground z-10" />
                      <Select
                        value={inviteData.role}
                        onValueChange={function(value) { setInviteData(Object.assign({}, inviteData, { role: value })); }}
                      >
                        <SelectTrigger className="pl-10" data-testid="invite-role-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="user">Utilisateur standard</SelectItem>
                          <SelectItem value="lawyer">Juriste / Avocat</SelectItem>
                          <SelectItem value="admin">Administrateur</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={closeInviteDialog}>
                    Annuler
                  </Button>
                  <Button onClick={handleInvite} disabled={inviting} data-testid="send-invite-btn">
                    {inviting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    Envoyer l'invitation
                  </Button>
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>

        <Dialog open={createDialog.open} onOpenChange={function(open) { if (!open) closeCreateDialog(); else setCreateDialog({ open }); }}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Créer un utilisateur</DialogTitle>
              <DialogDescription>
                Un mot de passe par défaut sera généré. L'utilisateur devra le changer à sa première connexion.
              </DialogDescription>
            </DialogHeader>

            {createdResult ? (
              <div className="space-y-6 py-4">
                <div className="flex items-center justify-center">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-8 h-8 text-green-600" />
                  </div>
                </div>
                <div className="text-center">
                  <h3 className="font-semibold text-lg mb-1">Utilisateur créé !</h3>
                  <p className="text-muted-foreground text-sm">{createdResult.email}</p>
                </div>
                <div className="space-y-2">
                  <Label>Mot de passe par défaut</Label>
                  <div className="flex gap-2">
                    <Input
                      readOnly
                      value={createdResult.default_password}
                      className="font-mono"
                      data-testid="generated-password"
                    />
                    <Button variant="outline" onClick={function() { copyPassword(createdResult.default_password); }}>
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                  <p className="text-xs text-amber-600">
                    Communiquez ce mot de passe à l'utilisateur. Il ne sera plus affiché ensuite.
                  </p>
                </div>
                <DialogFooter>
                  <Button onClick={closeCreateDialog} data-testid="close-create-dialog-btn">Fermer</Button>
                </DialogFooter>
              </div>
            ) : (
              <>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="create-name">Nom complet</Label>
                    <Input
                      id="create-name"
                      placeholder="Jean Dupont"
                      value={createData.name}
                      onChange={function(e) { setCreateData(Object.assign({}, createData, { name: e.target.value })); }}
                      data-testid="create-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="create-email">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="create-email"
                        type="email"
                        placeholder="utilisateur@email.com"
                        value={createData.email}
                        onChange={function(e) { setCreateData(Object.assign({}, createData, { email: e.target.value })); }}
                        className="pl-10"
                        data-testid="create-email-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="create-role">Niveau d'accès</Label>
                    <div className="relative">
                      <Shield className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground z-10" />
                      <Select
                        value={createData.role}
                        onValueChange={function(value) { setCreateData(Object.assign({}, createData, { role: value })); }}
                      >
                        <SelectTrigger className="pl-10" data-testid="create-role-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="user">Utilisateur standard</SelectItem>
                          <SelectItem value="lawyer">Juriste / Avocat</SelectItem>
                          <SelectItem value="admin">Administrateur</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={closeCreateDialog}>Annuler</Button>
                  <Button onClick={handleCreateUser} disabled={creating} data-testid="submit-create-user-btn">
                    {creating && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    Créer l'utilisateur
                  </Button>
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AppLayout>
  );
}
